import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import MegaMenu from './MegaMenu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [closeTimeout, setCloseTimeout] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prefetch products for faster megamenu loading
  useEffect(() => {
    const prefetchProducts = async () => {
      try {
        // Check if products already cached in sessionStorage
        const cached = sessionStorage.getItem('products_cache');
        if (!cached) {
          // Import contentAPI dynamically to avoid circular dependencies
          const { contentAPI } = await import('../services/api');
          const products = await contentAPI.getProducts();
          sessionStorage.setItem('products_cache', JSON.stringify(products));
          sessionStorage.setItem('products_cache_time', Date.now().toString());
        }
      } catch (error) {
        console.error('Error prefetching products:', error);
      }
    };
    
    // Prefetch after a short delay to not block initial page load
    const timer = setTimeout(prefetchProducts, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleMenuEnter = (menuKey) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setActiveMegaMenu(menuKey);
  };

  const handleMenuLeave = () => {
    const timeout = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 300);
    setCloseTimeout(timeout);
  };

  const menuItems = [
    { title: 'SAP Business One', hasMegaMenu: true, megaMenuType: 'sap-business-one' },
    { title: 'SAP B1 Extensions', hasMegaMenu: true, megaMenuType: 'products' },
    { title: 'Who We Are', hasMegaMenu: true, megaMenuType: 'who-we-are' },
    { title: 'The Ascarii Experience', path: '/experience' },
    { title: 'How We Can Help', hasMegaMenu: true, megaMenuType: 'services' }
  ];

  return (
    <>
      <nav className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group focus:outline-none">
              <img 
                src="/ascarii-logo.png"
                srcSet="/ascarii-logo.png 1x, /ascarii-logo@2x.png 2x"
                alt="Ascarii Logo" 
                className="h-8 w-auto group-hover:scale-105 transition-transform duration-300"
                style={{ imageRendering: 'auto' }}
              />
            </Link>

            {/* Desktop Menu */}
            <div 
              className="hidden lg:flex items-center space-x-1"
              onMouseLeave={handleMenuLeave}
            >
              {menuItems.map((item, index) => (
                item.hasMegaMenu ? (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => handleMenuEnter(item.megaMenuType)}
                  >
                    <button className="px-4 py-2 text-slate-700 hover:text-sky-600 font-medium flex items-center gap-1 transition-colors focus:outline-none">
                      {item.title}
                      <ChevronDown className={`w-4 h-4 transition-transform ${
                        activeMegaMenu === item.megaMenuType ? 'rotate-180' : ''
                      }`} />
                    </button>
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={item.path}
                    className="px-4 py-2 text-slate-700 hover:text-sky-600 font-medium transition-colors focus:outline-none"
                  >
                    {item.title}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Buttons - Swapped Order */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/contact" className="focus:outline-none">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium shadow-sm focus:outline-none">
                  Get in Touch
                </Button>
              </Link>
              <Link to="/portal/login" className="focus:outline-none">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-lg font-medium focus:outline-none">
                  Login
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-700 hover:text-sky-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mega Menu - Desktop Only */}
        <AnimatePresence>
          {activeMegaMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-gray-200 shadow-2xl"
              transition={{ duration: 0.2 }}
              className="absolute left-0 right-0 top-full bg-white border-t shadow-2xl"
              style={{ marginTop: '0px' }}
              onMouseEnter={() => {
                if (closeTimeout) {
                  clearTimeout(closeTimeout);
                  setCloseTimeout(null);
                }
              }}
              onMouseLeave={handleMenuLeave}
            >
              <div className="container mx-auto px-4 lg:px-8 py-6">
                <MegaMenu type={activeMegaMenu} onClose={() => setActiveMegaMenu(null)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b shadow-lg overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 max-h-[80vh] overflow-y-auto">
              {menuItems.map((item, index) => (
                <MobileMenuItem 
                  key={index} 
                  item={item} 
                  onClose={() => setIsMobileMenuOpen(false)} 
                />
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="mt-6 space-y-3 pt-6 border-t border-gray-200">
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Get in Touch
                  </Button>
                </Link>
                <Link to="/portal/login" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600">
                    Login
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Mobile Menu Item Component with Submenu Support
const MobileMenuItem = ({ item, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Define submenu items for each megamenu type
  const submenuItems = {
    'sap-business-one': [
      { name: 'SAP B1 Showcase', path: '/sap-business-one' },
      { name: 'Why SAP B1', path: '/sap-business-one/why' },
      { name: 'Manufacturing', path: '/sap-business-one/industries/manufacturing' },
      { name: 'Wholesale Distribution', path: '/sap-business-one/industries/wholesale' },
      { name: 'Professional Services', path: '/sap-business-one/industries/professional-services' },
      { name: 'Cloud Hosting', path: '/cloud-hosting' },
      { name: 'For Managing Directors', path: '/sap-business-one/for/managing-directors' },
      { name: 'For Finance Directors', path: '/sap-business-one/for/finance-directors' },
      { name: 'For IT Directors', path: '/sap-business-one/for/it-directors' }
    ],
    'products': [
      { name: 'Beas Manufacturing', path: '/products/beas-manufacturing' },
      { name: 'Netronic Manufacturing', path: '/products/netronic-manufacturing' },
      { name: 'Produmex WMS', path: '/products/produmex-wms' },
      { name: 'Produmex Scan', path: '/products/produmex-scan' },
      { name: 'FocusPoint', path: '/products/focuspoint' },
      { name: 'B1 Usability Package', path: '/products/b1-usability-package' },
      { name: 'B1 InterCompany', path: '/products/b1-intercompany' },
      { name: 'CRM for Outlook', path: '/products/crm-for-outlook' },
      { name: 'BPA Platform', path: '/products/bpa-platform' },
      { name: 'Magma', path: '/products/magma' }
    ],
    'who-we-are': [
      { name: 'About Us', path: '/about' },
      { name: 'Blog/News', path: '/blog' },
      { name: 'FAQs', path: '/faqs' }
    ],
    'services': [
      { name: 'Implementation', path: '/services/implementation' },
      { name: 'Support', path: '/services/support' }
    ]
  };

  const hasSubmenu = item.hasMegaMenu;
  const subitems = hasSubmenu ? submenuItems[item.megaMenuType] || [] : [];

  if (hasSubmenu) {
    return (
      <div className="mb-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-sky-50 rounded-lg transition-colors font-medium"
        >
          <span>{item.title}</span>
          <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="pl-4 pr-4 py-2 space-y-1">
                {subitems.map((subitem, idx) => (
                  <Link
                    key={idx}
                    to={subitem.path}
                    className="block px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-600 rounded-lg transition-colors"
                    onClick={onClose}
                  >
                    {subitem.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      className="block px-4 py-3 text-slate-700 hover:bg-sky-50 hover:text-sky-600 rounded-lg transition-colors font-medium"
      onClick={onClose}
    >
      {item.title}
    </Link>
  );
};

export default Header;